import { useEffect, useRef } from "react";
import { useAnimation, useInView, motion } from "framer-motion";
import { CompontHeader } from "./compont-header";
import { teamMembers } from "../data/team-member";

export const Team = () => {
  return (
    <div
      id="menue"
      className="pt-[84px] relative mx-auto w-full max-w-3xl gap-8 px-4"
    >
      <div className="pb-6">
        <CompontHeader text="Das Team" />
      </div>

      <p className="text-center text-3xl pb-16">
        Familienbetrieb - Die Brüder vom Grill
      </p>

      <div className="space-y-0">
        {teamMembers.map((member, index) => (
          <section key={member.name} className="px-4 py-16 sm:px-12">
            {/* Mobile Layout */}
            <div className="sm:hidden flex flex-col items-center space-y-8">
              <div className="text-center">
                <Reveal>
                  <h2 className="my-2 text-4xl text-slate-400">
                    {member.name}
                  </h2>
                </Reveal>
                <Reveal>
                  <p className="max-w-xl text-xl text-slate-300">
                    {member.position}
                  </p>
                </Reveal>
              </div>
              <div className="w-full max-w-[300px] mx-auto">
                <img
                  src={member.image}
                  alt={member.name}
                  className="rounded-xl w-full h-auto object-cover"
                />
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden sm:block">
              <div className="relative mx-auto max-w-6xl h-[400px]">
                {/* Text Container */}
                <div
                  className={`absolute z-10 ${
                    index % 2 === 0 ? "left-0" : "right-0"
                  } top-1/2 -translate-y-1/2 max-w-[50%]`}
                >
                  <div
                    className={`flex flex-col ${
                      index % 2 === 1 ? "items-end" : "items-start"
                    }`}
                  >
                    <Reveal>
                      <h2
                        className={`my-2 text-6xl text-slate-400 ${
                          index % 2 === 1 ? "text-right" : "text-left"
                        }`}
                      >
                        {member.name}
                      </h2>
                    </Reveal>
                    <Reveal>
                      <p
                        className={`text-2xl text-slate-300 ${
                          index % 2 === 1 ? "text-right" : "text-left"
                        }`}
                      >
                        {member.position}
                      </p>
                    </Reveal>
                  </div>
                </div>

                {/* Image Container */}
                <div
                  className={`absolute ${
                    index % 2 === 0 ? "right-0" : "left-0"
                  } top-1/2 -translate-y-1/2 max-w-[45%]`}
                >
                  <img
                    src={member.image}
                    alt={member.name}
                    className="rounded-xl w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

const ImgGrid = ({
  src,
  position,
}: {
  src: string;
  position: "left" | "right";
}) => {
  return (
    <div
      className={`absolute ${position}-0 top-[50%] z-0 max-w-[75%] -translate-y-[50%]`}
    >
      <img
        src={src}
        alt="team member"
        className="rounded-xl w-full h-auto object-cover"
      />
    </div>
  );
};

interface RevealProps {
  children: JSX.Element;
}

export const Reveal = ({ children }: RevealProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  const mainControls = useAnimation();
  const slideControls = useAnimation();

  useEffect(() => {
    if (isInView) {
      mainControls.start("visible");
      slideControls.start("visible");
    }
  }, [isInView]);

  return (
    <div ref={ref} className="relative w-fit overflow-hidden">
      <motion.div
        variants={{
          hidden: { opacity: 0, y: 75 },
          visible: { opacity: 1, y: 0 },
        }}
        initial="hidden"
        animate={mainControls}
        transition={{ duration: 0.5, delay: 0.25 }}
      >
        {children}
      </motion.div>
      <motion.div
        variants={{
          hidden: { left: 0 },
          visible: { left: "100%" },
        }}
        initial="hidden"
        animate={slideControls}
        transition={{ duration: 0.5, ease: "easeIn" }}
        className="absolute bottom-1 left-0 right-0 top-1 z-20 bg-[#FAED04]"
      />
    </div>
  );
};
